import { parseCoastalForecast, type CoastalForecast } from '../lib/cwfParse';

/**
 * NWS marine products for the Bay and the waters our stations sit in.
 *
 * `api.weather.gov` is keyless and CORS-enabled but insists on a descriptive
 * User-Agent. Note that `/zones/forecast/{id}/forecast` returns 404 for marine
 * zones — "not yet supported" — so the forecast comes from the raw Coastal
 * Waters Forecast text product instead, which is reliably structured.
 */

const API = 'https://api.weather.gov';
const UA = 'sfbay-wind (github.com/sfbay-wind)';
/** Monterey WFO issues the Coastal Waters Forecast covering the Bay. */
const OFFICE = 'MTR';

/**
 * Every zone the San Francisco CWF product covers, nearest water first.
 *
 * The alert query uses this same list. A zone whose forecast is on screen must
 * also be in scope for alerts, or its "GALE WARNING IN EFFECT" headline would
 * have no warning to open.
 */
export const MARINE_ZONES = [
  { id: 'PZZ530', short: 'Bay north of the Bridge' },
  { id: 'PZZ531', short: 'Bay south of the Bridge' },
  { id: 'PZZ545', short: 'Pt Reyes to Pigeon Point' },
  { id: 'PZZ540', short: 'Pt Arena to Pt Reyes' },
  { id: 'PZZ535', short: 'Monterey Bay' },
  { id: 'PZZ560', short: 'Pigeon Pt to Pt Pinos' },
  { id: 'PZZ565', short: 'Pt Pinos to Piedras Blancas' },
  { id: 'PZZ571', short: 'Pt Reyes to Pigeon Pt, 10-60 NM' },
  { id: 'PZZ570', short: 'Pt Arena to Pt Reyes, 10-60 NM' },
  { id: 'PZZ575', short: 'Pigeon Pt to Pt Pinos, 10-60 NM' },
  { id: 'PZZ576', short: 'Pt Pinos to Piedras Blancas, 10-60 NM' },
] as const;

/**
 * The label to head a zone's section with.
 *
 * The hand-written short names cover the Bay's own zones only. Every other
 * region falls back to the name the product itself prints, which is why adding
 * forty-one more regions needed no new labels.
 */
export function zoneLabel(zoneId: string, fallback: string): string {
  return MARINE_ZONES.find((z) => z.id === zoneId)?.short ?? fallback;
}

/** The water this site is actually about — everything else is context. */
export const BAY_ZONES = ['PZZ530', 'PZZ531'];

export function isBayZone(zoneIds: string[]): boolean {
  return zoneIds.some((z) => BAY_ZONES.includes(z));
}

async function get(path: string): Promise<unknown> {
  const res = await fetch(`${API}${path}`, { headers: { 'User-Agent': UA } });
  if (!res.ok) throw new Error(`NWS HTTP ${res.status}`);
  return res.json();
}

export interface MarineAlert {
  id: string;
  event: string;
  severity: string;
  headline: string;
  areaDesc: string;
  /** The "* WHAT..." body, already unwrapped. */
  description: string;
  onset: Date | null;
  ends: Date | null;
  zones: string[];
}

/** Cached per office: switching regions and back must not refetch. */
const forecastCache = new Map<string, Promise<{ forecast: CoastalForecast; issued: Date }>>();

/**
 * The Coastal Waters Forecast for one office, parsed. Reissued a few times a day.
 *
 * Forty-two offices publish this same product and the parser handles all of
 * them; only the office changes. Two of them — Los Angeles and Seattle — omit
 * the area synopsis, so callers must tolerate an empty one.
 */
export function coastalForecast(
  office: string = OFFICE,
): Promise<{ forecast: CoastalForecast; issued: Date }> {
  const hit = forecastCache.get(office);
  if (hit) return hit;

  const pending = (async () => {
    const list = (await get(`/products/types/CWF/locations/${office}`)) as {
      '@graph': Array<{ id: string; issuanceTime: string }>;
    };
    const newest = list['@graph']?.[0];
    if (!newest) throw new Error('No coastal waters forecast published');

    const product = (await get(`/products/${newest.id}`)) as { productText: string };
    return {
      forecast: parseCoastalForecast(product.productText),
      issued: new Date(newest.issuanceTime),
    };
  })().catch((err) => {
    forecastCache.delete(office); // Let a transient failure be retried.
    throw err;
  });

  forecastCache.set(office, pending);
  return pending;
}

/** Active warnings and advisories over our zones, worst first. */
export async function marineAlerts(zoneIds?: readonly string[]): Promise<MarineAlert[]> {
  // A zone whose forecast is on screen must also be in scope for alerts, or its
  // "GALE WARNING IN EFFECT" headline would have no warning to open. So the
  // caller passes the zones of whichever region is showing.
  const zones = (zoneIds ?? MARINE_ZONES.map((z) => z.id)).join(',');
  const body = (await get(`/alerts/active?zone=${zones}`)) as {
    features: Array<{ properties: Record<string, string | string[] | null> }>;
  };

  const rank: Record<string, number> = {
    Extreme: 0, Severe: 1, Moderate: 2, Minor: 3, Unknown: 4,
  };

  return (body.features ?? [])
    .map((f) => {
      const p = f.properties;
      const str = (k: string) => (typeof p[k] === 'string' ? (p[k] as string) : '');
      return {
        id: str('id'),
        event: str('event'),
        severity: str('severity'),
        headline: str('headline'),
        areaDesc: str('areaDesc'),
        description: str('description').replace(/\s+/g, ' ').trim(),
        onset: str('onset') ? new Date(str('onset')) : null,
        ends: str('ends') || str('expires') ? new Date(str('ends') || str('expires')) : null,
        zones: ((p.affectedZones as string[]) ?? []).map((z) => z.split('/').pop() ?? ''),
      };
    })
    .sort((a, b) => (rank[a.severity] ?? 9) - (rank[b.severity] ?? 9));
}

const zoneGeometry = new Map<string, Promise<GeoJSON.Geometry | null>>();

/**
 * The polygon for a marine zone, so an alert can be drawn where it applies.
 *
 * Alerts themselves carry no inline geometry — they reference zones — and the
 * zone outlines are large (PZZ530 alone is a 35-ring polygon), so each is
 * fetched once and kept.
 */
export function zoneShape(zoneId: string): Promise<GeoJSON.Geometry | null> {
  const hit = zoneGeometry.get(zoneId);
  if (hit) return hit;

  const pending = get(`/zones/forecast/${zoneId}`)
    .then((z) => (z as { geometry: GeoJSON.Geometry | null }).geometry ?? null)
    .catch(() => {
      zoneGeometry.delete(zoneId);
      return null;
    });

  zoneGeometry.set(zoneId, pending);
  return pending;
}

/**
 * Local Notice to Mariners, District 11.
 *
 * There is no machine-readable feed: the Coast Guard publishes LNM as weekly
 * PDFs and states a GeoJSON release is planned with no date attached. Rather
 * than scrape a PDF and risk misreporting a navigation hazard, this is a link
 * out to the authoritative source.
 */
export const LNM_DISTRICT_11 =
  'https://www.navcen.uscg.gov/local-notices-to-mariners?district=11+0&subdistrict=n';
